import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CalendarEventType,
  GroupTagType,
  Print_TimetableLayout,
  type Print_TimetableOptions,
  Print_TimetablePeriodDisplayInCell,
  Print_TimetablePeriodDisplayOnAxis,
  Print_TimetableStaffFormat,
  Print_TimetableSubjectFormat,
} from '@tyro/api';
import {
  LoadingPlaceholderContainer,
  RHFCheckboxGroup,
  RHFDatePicker,
  RHFRadioGroup,
  RHFSelect,
  RHFSwitch,
} from '@tyro/core';
import { getCatalogueGroupTags } from '@tyro/groups';
import { useTranslation } from '@tyro/i18n';
import { getStaffForSelect } from '@tyro/people';
import { getCoreRooms } from '@tyro/settings';
import { getTtClassGroups } from '@tyro/timetable';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  getPrintTimetable,
  usePrintTimetable,
} from '../../api/print-timetable';

const checkboxOptions = [
  'showRooms',
  'showGroupNames',
  'hideSubstitution',
] as const;

enum FontSize {
  EXCEEDINGLY_SMALL = 'EXCEEDINGLY_SMALL',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export interface PrintStaffTimetableFormState<T> {
  selectedOptions: T[];
  allStaff?: boolean;
  allRooms?: boolean;
  allTtClassGroupIds?: boolean;
  allGroupTagIds?: boolean;
  teacherDisplayOption: Print_TimetableStaffFormat;
  layout: Print_TimetableLayout;
  periodDisplayOnAxis: Print_TimetablePeriodDisplayOnAxis;
  periodDisplayInCell: Print_TimetablePeriodDisplayInCell;
  subjectFormat: Print_TimetableSubjectFormat;
  individualStudents?: boolean;
  fontSize: FontSize;
  colorSetting: 'COLOR' | 'BLACK_AND_WHITE';
  checkBoxes: (typeof checkboxOptions)[number][];
  eventTypes: CalendarEventType[];
  weekOf: dayjs.Dayjs;
  invert: boolean;
}

export interface TimetablePrintFormProps<T> {
  translateIds: (partiesOrRooms: T[]) => Partial<Print_TimetableOptions>;
  timetableId?: number;
  showEventTypes?: boolean;
}

function mapFontSize(fontSize: FontSize) {
  switch (fontSize) {
    case FontSize.EXCEEDINGLY_SMALL:
      return 6;
    case FontSize.SMALL:
      return 10;
    case FontSize.LARGE:
      return 18;
    default: // MEDIUM
      return 12;
  }
}

export function getDefaultValues<T>(
  defaultValueOverrides: Partial<PrintStaffTimetableFormState<T>> = {},
) {
  return {
    selectedOptions: [],
    showRooms: true,
    teacherDisplayOption: Print_TimetableStaffFormat.Full,
    layout: Print_TimetableLayout.DaysOnXAxis,
    showGroupNames: true,
    hideSubstitution: true,
    periodDisplayOnAxis: Print_TimetablePeriodDisplayOnAxis.Time,
    periodDisplayInCell: Print_TimetablePeriodDisplayInCell.Hide,
    subjectFormat: Print_TimetableSubjectFormat.Full,
    fontSize: FontSize.MEDIUM,
    checkBoxes: ['showRooms', 'showGroupNames'],
    eventTypes: [CalendarEventType.General, CalendarEventType.Lesson],
    colorSetting: 'COLOR',
    weekOf: dayjs(),
    invert: false,
    ...defaultValueOverrides,
  } as PrintStaffTimetableFormState<T>;
}

export function TimetablePrintForm<T>({
  translateIds,
  timetableId,
  showEventTypes = true,
}: TimetablePrintFormProps<T>) {
  const { t } = useTranslation(['printing', 'common', 'calendar']);

  const [filter, setFilter] = useState<Print_TimetableOptions>({
    timetableId,
    partyIds: [1],
    showRooms: true,
    teacherDisplayOption: Print_TimetableStaffFormat.Full,
    layout: Print_TimetableLayout.DaysOnXAxis,
    showGroupNames: true,
    hideSubstitution: true,
    periodDisplayOnAxis: Print_TimetablePeriodDisplayOnAxis.Time,
    periodDisplayInCell: Print_TimetablePeriodDisplayInCell.Hide,
    subjectFormat: Print_TimetableSubjectFormat.Full,
    individualStudents: false,
    fontSize: 15,
    printWithColour: true,
    invert: false,
  });

  const { control, handleSubmit } =
    useFormContext<PrintStaffTimetableFormState<T>>();
  const { data: timetableData, isFetching } = usePrintTimetable(filter);

  const getTimetableOptions = async ({
    selectedOptions,
    allStaff,
    allRooms,
    allTtClassGroupIds,
    allGroupTagIds,
    layout,
    teacherDisplayOption,
    periodDisplayOnAxis,
    periodDisplayInCell,
    subjectFormat,
    individualStudents,
    fontSize,
    colorSetting,
    checkBoxes,
    eventTypes,
    weekOf,
    invert,
  }: PrintStaffTimetableFormState<T>): Promise<Print_TimetableOptions> => {
    const translatedOptions = translateIds(selectedOptions) ?? {};

    if (allStaff) {
      const { core_staff: coreStaff } = await getStaffForSelect({
        availableForTeaching: true,
      });

      translatedOptions.partyIds = coreStaff.map(({ partyId }) => partyId);
    }

    if (allRooms) {
      const { core_rooms: coreRooms } = await getCoreRooms({});
      translatedOptions.roomIds = (coreRooms ?? []).map(({ roomId }) => roomId);
    }

    if (allTtClassGroupIds) {
      const { tt_classGroups: classGroups } = await getTtClassGroups({
        timetableId: timetableId!,
      });
      translatedOptions.ttClassGroupIds = classGroups.map(
        ({ ttClassGroupId }) => ttClassGroupId,
      );
    }

    if (allGroupTagIds) {
      const { catalogue_groupTags: groupTags } = await getCatalogueGroupTags({
        tagTypes: [
          GroupTagType.ProgrammeStage,
          GroupTagType.SupportGrouping,
          GroupTagType.Meeting,
        ],
      });
      translatedOptions.groupTagIds = groupTags.map(({ id }) => id);
    }

    return {
      timetableId,
      ...translatedOptions,
      teacherDisplayOption,
      layout,
      ...checkboxOptions.reduce(
        (acc, option) => {
          acc[option] = checkBoxes.includes(option);
          return acc;
        },
        {} as Pick<
          Print_TimetableOptions,
          'showRooms' | 'showGroupNames' | 'hideSubstitution'
        >,
      ),
      periodDisplayOnAxis,
      periodDisplayInCell,
      subjectFormat,
      individualStudents: individualStudents ?? false,
      fontSize: mapFontSize(fontSize),
      printWithColour: colorSetting === 'COLOR',
      printWeekOf: weekOf.format('YYYY-MM-DD'),
      calendarEventTypes: showEventTypes ? eventTypes : undefined,
      invert,
    };
  };

  const onView = handleSubmit(async (formState) => {
    const timetableOptions = await getTimetableOptions(formState);
    setFilter({
      ...timetableOptions,
      hideSubstitution: !timetableOptions.hideSubstitution,
    });
  });

  const handlePrint = handleSubmit(async (formState) => {
    const timetableOptions = await getTimetableOptions(formState);
    const printResponse = await getPrintTimetable({
      ...timetableOptions,
      hideSubstitution: !timetableOptions.hideSubstitution,
    });

    if (printResponse?.print_printTimetable?.url)
      window.open(
        printResponse.print_printTimetable.url,
        '_blank',
        'noreferrer',
      );
  });

  const showWeekOfField = !timetableId;

  return (
    <Stack spacing={2} pt={2}>
      <form onSubmit={onView}>
        <Stack direction="row" spacing={2}>
          <Card sx={{ py: 1.5, px: 2.5, flex: 1 }}>
            <Typography variant="h6" sx={{ pb: 2 }}>
              {t('printing:selectInfoToPrint')}
            </Typography>
            <Stack direction="row" spacing={4}>
              <Stack spacing={2} flex={1} maxWidth={300}>
                <RHFSelect<
                  PrintStaffTimetableFormState<T>,
                  Print_TimetableStaffFormat
                >
                  fullWidth
                  label={t('printing:timetable.options.teacherDisplayFormat')}
                  options={Object.values(Print_TimetableStaffFormat)}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.teacherDisplayOptions.${option}`)
                  }
                  controlProps={{
                    name: 'teacherDisplayOption',
                    control,
                  }}
                />
                <RHFSelect<
                  PrintStaffTimetableFormState<T>,
                  Print_TimetableLayout
                >
                  fullWidth
                  label={t('printing:timetable.options.layout')}
                  options={Object.values(Print_TimetableLayout)}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.layout.${option}`)
                  }
                  controlProps={{
                    name: 'layout',
                    control,
                  }}
                />
                <RHFSelect<
                  PrintStaffTimetableFormState<T>,
                  Print_TimetablePeriodDisplayOnAxis
                >
                  fullWidth
                  label={t('printing:timetable.options.periodDisplayOnAxis')}
                  options={Object.values(Print_TimetablePeriodDisplayOnAxis)}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.periodDisplayOnAxis.${option}`)
                  }
                  controlProps={{
                    name: 'periodDisplayOnAxis',
                    control,
                  }}
                />
                <RHFSwitch
                  label={t('printing:showWhenFree')}
                  controlProps={{
                    name: 'invert',
                    control,
                  }}
                />
              </Stack>
              <Stack spacing={2} flex={1} maxWidth={300}>
                <RHFSelect<
                  PrintStaffTimetableFormState<T>,
                  Print_TimetablePeriodDisplayInCell
                >
                  fullWidth
                  label={t('printing:timetable.options.periodDisplayInCell')}
                  options={Object.values(Print_TimetablePeriodDisplayInCell)}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.periodDisplayInCell.${option}`)
                  }
                  controlProps={{
                    name: 'periodDisplayInCell',
                    control,
                  }}
                />
                {showWeekOfField && (
                  <RHFDatePicker
                    label={t('printing:timetable.weekOf')}
                    controlProps={{
                      control,
                      name: 'weekOf',
                    }}
                    inputProps={{
                      fullWidth: true,
                    }}
                  />
                )}

                <RHFSelect<
                  PrintStaffTimetableFormState<T>,
                  Print_TimetableSubjectFormat
                >
                  fullWidth
                  label={t('printing:timetable.options.subjectFormat')}
                  options={Object.values(Print_TimetableSubjectFormat)}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.subjectFormat.${option}`)
                  }
                  controlProps={{
                    name: 'subjectFormat',
                    control,
                  }}
                />
              </Stack>
              <Stack spacing={2}>
                <RHFCheckboxGroup
                  label={t('printing:includeAdditionalInformation')}
                  controlProps={{ name: 'checkBoxes', control }}
                  options={[...checkboxOptions]}
                  getOptionLabel={(option) =>
                    t(`printing:timetable.checkboxes.${option}`)
                  }
                />
                {showEventTypes && (
                  <RHFCheckboxGroup
                    label={t('calendar:eventTypes')}
                    controlProps={{ name: 'eventTypes', control }}
                    options={Object.values(CalendarEventType)}
                    getOptionLabel={(option) =>
                      t(`calendar:calendarEventTypes.${option}`)
                    }
                  />
                )}
              </Stack>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Card
              sx={{ py: 1.5, px: 2.5, backgroundColor: 'slate.100', flex: 1 }}
              variant="outlined"
            >
              <Typography variant="h6" sx={{ pb: 2 }}>
                {t('printing:printSettings.title')}
              </Typography>
              <Stack spacing={1}>
                <RHFRadioGroup
                  label={t('printing:printSettings.colourSettings.title')}
                  disabled={isFetching}
                  options={(['COLOR', 'BLACK_AND_WHITE'] as const).map(
                    (option) => ({
                      value: option,
                      label: t(
                        `printing:printSettings.colourSettings.${option}`,
                      ),
                    }),
                  )}
                  controlProps={{
                    name: 'colorSetting',
                    control,
                  }}
                />
                <RHFRadioGroup
                  label={t('printing:printSettings.fontSize.title')}
                  disabled={isFetching}
                  options={Object.values(FontSize).map((option) => ({
                    value: option,
                    label: t(`printing:printSettings.fontSize.${option}`),
                  }))}
                  controlProps={{
                    name: 'fontSize',
                    control,
                  }}
                />
              </Stack>
            </Card>
            <Stack direction="row" spacing={2}>
              <Button
                variant="soft"
                type="submit"
                loading={isFetching}
                sx={{ flex: 1 }}
              >
                {t('common:actions.view')}
              </Button>
              <Button
                variant="contained"
                loading={isFetching}
                onClick={handlePrint}
                sx={{ flex: 1 }}
              >
                {t('common:actions.print')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </form>
      <Card>
        <Stack
          style={{
            maxHeight: '100%',
            overflow: 'auto',
            padding: '16px',
            paddingTop: '8px',
            minHeight: '180px',
          }}
        >
          {timetableData?.html ? (
            <div
              style={{ maxHeight: '100%', overflow: 'auto' }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Need it for printing
              dangerouslySetInnerHTML={{ __html: timetableData.html }}
            />
          ) : (
            <LoadingPlaceholderContainer isLoading={isFetching}>
              <Stack
                sx={{
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h2" component="span">
                  ⚙
                </Typography>
                <Typography
                  variant="body1"
                  component="span"
                  color="text.secondary"
                >
                  {t('printing:pleaseSelectPrintSettings')}
                </Typography>
              </Stack>
            </LoadingPlaceholderContainer>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
